import {Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation} from '@angular/core';
import { OfferCardDataPrice, OfferCardSubtype, OfferCardType } from "../../offer-card-data.model";
import { AnimationService } from 'projects/common/services/animations.service';
import { AbstractOfferCard } from '../abstract-offer-card.component';

const imageSizes = {
  [OfferCardSubtype.SIMPLIFIED_HALF]: 'small',
  [OfferCardSubtype.SIMPLIFIED_ROW]: 'normal',
  [OfferCardSubtype.SIMPLIFIED_SQUARE]: 'large',
  [OfferCardSubtype.SIMPLIFIED_DESTINATION_EXPLORER]: 'normal'
};

@Component({
  selector: 'simplified-offer-card',
  template: `
    <offer-card class="card-display-{{cardDisplay}}" style="--image-base-size: {{this.imageBaseSize}};"
      [moduleId]="moduleId" [moduleIndex]="moduleIndex" [uniqueId]="uniqueId" [positionIndex]="positionIndex"
      [data]="data" [currency]="currency" [locale]="locale"
      [loyaltyMode]="loyaltyMode" [customPricebox]="true" [offerCardType]="offerCardType"
      [flow]="flow" [product]="product" [moduleReferer]="moduleReferer" (offerClicked)="onOfferClicked()" >

      <offer-image offer-image
        [mediaKey]="this.data.picture_link" 
        [product]="product" [size]="this.imageSize" 
        [alt]="data.product_text + ' - ' + data.main_text">
      </offer-image>

      <div class="main-text -eva-3-ellipsis" offer-content>{{this.data.main_text}}</div>

      <div *ngIf="this.price" class="pricebox" custom-pricebox>
        <p class="pricebox-description" [innerHTML]="this.price.message | breakLine: this.lineBreakLimit">{{this.price.message}}</p>
        <div class="pricebox-price">
          <div class="pricebox-price-current">
            <span class="pricebox-currency">{{this.currency.symbol}}</span>
            <span class="pricebox-amount" [ngClass]="{'price-animating': animating}">{{this.visiblePrice}}</span>
          </div>
          <span *ngIf="this.showDiscount" class="pricebox-price-old" [ngClass]="{'price-animating': fadeInOldPrice}">
            {{this.currency.symbol}} {{this.visibleOldPrice}}
          </span>
        </div>
      </div>

    </offer-card>
  `,
  styleUrls: ['./simplified-offer-card.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SimplifiedOfferCardComponent extends AbstractOfferCard implements OnInit, OnChanges {
  readonly offerCardType = OfferCardType.SIMPLIFIED;

  @Input() cardDisplay?: OfferCardSubtype;
  @Input() imageBaseSize: number = 100;
  @Input() lineBreakLimit: number | null = null;
  @Input() couponActivated: boolean = false;
  @Input() isMalbec: boolean = false;

  imageSize: 'small' | 'normal' | 'large';
  price: OfferCardDataPrice;
  visiblePrice: string;
  visibleOldPrice: string;
  
  showDiscount = false;
  animating = false;
  fadeInOldPrice = false;

  constructor(private animationService: AnimationService) { super(); }

  ngOnInit() {
    if (!this.cardDisplay) this.cardDisplay = OfferCardSubtype.SIMPLIFIED_ROW;
    this.price = this.data.price[this.currency.code];
    this.imageSize = imageSizes[this.cardDisplay];
    this.adjustVisiblePrices();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.shouldShowCouponAnimation(changes)) {
      this.adjustVisiblePrices();
      this.triggerPriceAnimation();
    }
  }

  adjustVisiblePrices() {
    if (!this.isMalbec && this.hasPromo()) {
      if (this.couponActivated) {
        this.showDiscount = true;
        this.visibleOldPrice = this.price.promo?.real_price;
        this.visiblePrice = this.price.total_amount;
      } else {
        this.showDiscount = false;
        this.visiblePrice = this.price.promo?.real_price;
      }
    } else {
      this.showDiscount = this.hasPromo();
      this.visiblePrice = this.price.total_amount;
      this.visibleOldPrice = this.price.promo?.real_price;
    }
  }

  private shouldShowCouponAnimation(changes: SimpleChanges): boolean {
    const couponChange = changes['couponActivated'];
    return couponChange?.previousValue === false 
        && couponChange?.currentValue === true 
        && this.hasPromo();
  }

  /**
   * Triggers the price animation when a coupon is activated.
   * @requires this.price not null
   * @requires this.price.promo not null
   */
  private triggerPriceAnimation(): void {
    requestAnimationFrame(() => {
      this.showDiscount = true;
      this.fadeInOldPrice = true;
      this.animating = true;
    });

    this.animationService.startNumericAnimation(
      this.price.promo.real_price_not_formatted,
      this.price.total_amount_not_formatted,
      (value) => this.visiblePrice = value,
    ).then(() => {
      this.adjustVisiblePrices();
      this.animating = false;
      this.fadeInOldPrice = false;
    });
  }

  private hasPromo(): boolean {
    return !!this.price?.promo?.real_price;
  }
}
